import { useEffect, useRef, useState } from 'react';
import { haptic } from '@/lib/telegram';
import styles from './PhotoLightbox.module.css';

interface Props {
  urls: string[];
  initialIndex?: number;
  isOpen: boolean;
  onClose: () => void;
}

export const PhotoLightbox = ({
  urls,
  initialIndex = 0,
  isOpen,
  onClose,
}: Props) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(initialIndex);

  useEffect(() => {
    if (!isOpen) return;

    setActiveIdx(initialIndex);

    const el = scrollerRef.current;
    if (!el) return;

    const scrollToIndex = () => {
      el.scrollLeft = initialIndex * el.clientWidth;
    };

    scrollToIndex();
    requestAnimationFrame(scrollToIndex);
  }, [isOpen, initialIndex]);

  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || !isOpen) return;

    const onScroll = () => {
      const idx = Math.round(el.scrollLeft / el.clientWidth);
      setActiveIdx(idx);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [isOpen]);

  if (!isOpen || urls.length === 0) return null;

  const handleClose = () => {
    haptic.light();
    onClose();
  };

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <button
        type="button"
        className={styles.close}
        onClick={handleClose}
        aria-label="Закрыть"
      >
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>

      {urls.length > 1 && (
        <div className={styles.counter}>
          <span className={styles.counterCurrent}>{activeIdx + 1}</span>
          <span className={styles.counterSep}>/</span>
          <span className={styles.counterTotal}>{urls.length}</span>
        </div>
      )}

      <div
        ref={scrollerRef}
        className={styles.scroller}
        onClick={(e) => e.stopPropagation()}
      >
        {urls.map((url, i) => (
          <div key={i} className={styles.slide}>
            <img src={url} alt="" className={styles.image} decoding="async" />
          </div>
        ))}
      </div>
    </div>
  );
};
